import { type FieldErrors } from 'react-hook-form';
import { OFPageType } from '../../generated/api/oFPageType.js';
import { OFQuestionType } from '../../generated/api/oFQuestionType.js';
import { OFRulesType } from '../../generated/api/oFRulesType.js';
import { has } from './OpenForm.js';
import { OpenFormMap } from './OpenFormMap.js';
import { isAvailabilityAddress, merge } from './OpenFormUtils.js';
import type { Address } from '../../generated/api/address.js';
import type { AdminAddressOption } from '../../generated/api/adminAddressOption.js';
import type { BillingAccount } from '../../generated/api/billingAccount.js';
import type { BillingAccountDeliveryMethod } from '../../generated/api/billingAccountDeliveryMethod.js';
import type { OFFieldApi } from '../../generated/api/oFFieldApi.js';
import type { OFProductAvailability } from '../../generated/api/oFProductAvailability.js';
import type { OpenFormChoice } from '../../generated/api/openFormChoice.js';
import type { OpenFormQuestion } from '../../generated/api/openFormQuestion.js';
import type { OpenFormSection } from '../../generated/api/openFormSection.js';
import type { ParameterizedSearchContactResult } from '../../generated/api/parameterizedSearchContactResult.js';

export type AvailabilityAddress = {
  match: AdminAddressOption;
  products: OFProductAvailability[];
};

export type BillingAccountNew = {
  accountMasterId?: string;
  billElectronicAddress?: string;
  billElectronicOperator?: string;
  billElectronicOperatorLabel?: string;
  billFormatType?: BillingAccount.BillFormatTypeEnum;
  billLanguage?: 'FI' | 'SV' | 'EN';
  billReceiverEmail?: string;
  billingAccountName?: string;
  billingContactAccountId?: string;
  billingContactAccountName?: string;
  billingContactEmail?: string;
  billingContactId?: string;
  billingContactName?: string;
  billingContactPhone?: string;
  billingAccountExtensionName?: string;
  customerReference1?: string;
  customerReference2?: string;
  deliveryMethod?: BillingAccountDeliveryMethod;
  payerAddress?: Address;
  payerBusinessId?: string;
  payerName?: string;
  payerNameExtension?: string;
};

export type Charges = Record<string, Record<string, string>>;

export type Choices = string[];

export type SubscriptionReferences = { [key in Extract<OFFieldApi, `Reference_Id_${1 | 2 | 3 | 4}__c`>]?: string };

export type SubscriptionTypes = string[];

export type OpenFormContext = {
  address?: AvailabilityAddress;
  billingAccountNew?: BillingAccountNew;
  billingAccountOld?: BillingAccount;
  charges?: Charges;
  choices?: Choices;
  contact?: ParameterizedSearchContactResult;
  subscriptionReferences?: SubscriptionReferences;
  subscriptionTypes?: SubscriptionTypes;
};

export class OpenFormAnswers extends OpenFormMap<string, OpenFormContext> {
  static isContext(key: unknown): key is keyof OpenFormContext {
    switch (key) {
      case 'address':
      case 'billingAccountNew':
      case 'billingAccountOld':
      case 'charges':
      case 'choices':
      case 'contact':
      case 'subscriptionReferences':
      case 'subscriptionTypes':
        return true;
      default:
        return false;
    }
  }

  #availability?: Record<string, boolean | undefined>;

  constructor(answers?: OpenFormAnswers | [string, OpenFormContext][] | Iterable<[string, OpenFormContext]> | null) {
    super(answers);
    this.#availability = undefined;
  }

  get availability() {
    return (this.#availability ??= merge(
      ({ choice, isAvailable }) => ({ [choice]: isAvailable }),
      ...(this.findOf('address', ({ address }) => isAvailabilityAddress(address))[1]?.products ?? [])
    ));
  }

  get(target: OpenFormSection | OpenFormQuestion | string | undefined) {
    return target === undefined ? target : super.get(typeof target === 'string' ? target : target.guid);
  }

  readonly getAvailability = ({ checkAvailability }: OpenFormQuestion) =>
    new Proxy<Record<string, boolean>>(
      {},
      { get: (_, choice: string) => (checkAvailability ? Boolean(this.availability[choice]) : true) }
    );

  readonly hasAnswer = (question: OpenFormQuestion) => {
    const context = this.get(question);
    if (!context) {
      return false;
    }
    switch (question.type) {
      case OFQuestionType.ADDITIONAL_INFORMATION_BACKOFFICE:
      case OFQuestionType.ADDITIONAL_INFORMATION_FIELD_ENGINEER:
      case OFQuestionType.BA_SELECTION:
      case OFQuestionType.DATE_OF_DELIVERY:
      case OFQuestionType.DELIVERY_CONTACT:
      case OFQuestionType.FAULT_INCIDENT_CONTACT:
      case OFQuestionType.FREE_TEXT:
      case OFQuestionType.INSTALLATION_ADDRESS:
      case OFQuestionType.INSTALLATION_CONTACT:
      case OFQuestionType.ORDERING_CONTACT:
      case OFQuestionType.TECHNICAL_CONTACT:
      case OFQuestionType.TIME_OF_DELIVERY:
        return Boolean(context.choices?.every(value => value.length));
      case OFQuestionType.CONTRACT_PERIOD:
      case OFQuestionType.LIST_OF_OBJECTS_MULTI_SELECT:
      case OFQuestionType.LIST_OF_OBJECTS_SINGLE_SELECT:
      case OFQuestionType.MULTI_SELECT:
      case OFQuestionType.SINGLE_SELECT:
        return Boolean(context.choices?.every(guid => question.choices.some(has(guid))));
      case OFQuestionType.SUBSCRIPTION_REFERENCE_ID:
        return Object.values(context.subscriptionReferences ?? {}).some(Boolean);
      default:
        return false;
    }
  };

  readonly hasAvailability = () =>
    this.hasOf('choices', ({ choices }) => choices.some(choice => this.availability[choice]));

  readonly hasError = (section: OpenFormSection | undefined, errors: FieldErrors) =>
    section?.questions.find(q => this.isVisible(q) && ((q.isMandatory && !this.hasAnswer(q)) || errors[q.guid]));

  readonly isActive = (section: OpenFormSection) => {
    switch (section.pageType) {
      case OFPageType.AVAILABILITY_CHECK:
      case OFPageType.ORDER_ENRICHMENT:
        return true;
      case OFPageType.UNAVAILABLE_PRODUCT_DETAILS:
        if (this.hasAvailability()) {
          return false;
        }
      // eslint-disable-next-line no-fallthrough
      default:
        return section.questions
          .filter(this.isVisible)
          .some(q => has(OFQuestionType.FREE_TEXT)(q) || q.choices.some(this.isVisible));
    }
  };

  readonly isComplete = (section: OpenFormSection) => {
    switch (section.pageType) {
      case OFPageType.AVAILABILITY_CHECK:
        return isAvailabilityAddress(this.get(section)?.address);
      case OFPageType.UNAVAILABLE_PRODUCT_DETAILS:
        if (this.hasAvailability()) {
          return true;
        }
      // eslint-disable-next-line no-fallthrough
      default:
        return section.questions.filter(q => this.isVisible(q) && q.isMandatory).every(this.hasAnswer);
    }
  };

  readonly isValid = (section: OpenFormSection, question: OpenFormQuestion) => {
    switch (section.pageType) {
      case OFPageType.UNAVAILABLE_PRODUCT_DETAILS:
        if (this.hasAvailability()) {
          return false;
        }
      // eslint-disable-next-line no-fallthrough
      default:
        return (
          this.isVisible(question) &&
          question.choices
            .filter(c => this.hasOf('choices', ({ choices }) => choices.includes(c.guid)))
            .every(this.isVisible)
        );
    }
  };

  readonly isVisible = <T extends OpenFormQuestion | OpenFormChoice>(target: T | undefined): target is T => {
    switch (target?.rulesType) {
      case OFRulesType.NONE:
        return true;
      case OFRulesType.VISIBILITY_CONDITIONS:
        return this.#someRules(target.rules);
      case OFRulesType.HIDING_CONDITIONS:
        return !this.#someRules(target.rules);
      default:
        return false;
    }
  };

  readonly #someRules = (rules: string[]) =>
    rules.some(value => {
      switch (true) {
        case value.startsWith('Product_Line'):
          return (
            (value = value.replace(/^[^:]*:*/, '')) &&
            this.hasOf('subscriptionTypes', ({ subscriptionTypes }) => subscriptionTypes.includes(value))
          );
        default:
          return value && this.hasOf('choices', ({ choices }) => choices.includes(value));
      }
    });
}
